<script setup lang="ts">
import {
    ref, computed, toRefs,
} from 'vue';
import { useAntennaMonitoringStore } from '@/stores/antennaMonitoringStore';
import { useAntennaWebSocket, useTranslation } from '@/composables';
import {
    BCard,
    BChip,
    BRow,
    BCol,
    BDataTable,
    BTableActions,
    BBtn,
    BIcon,
    BTooltip,
    BAutocomplete,
    LogList,
    AntennaLogDialog,
    AntennaCurrentStateModal,
} from '@/design-system';
import type { AntennaMonitoringStatsType } from '@/types/stores/AntennaMonitoringStoreTypes';
import { useCompanyStore } from '@/apps/super-admin/stores/companyStore';

type AntennaMonitoringContainerPropsType = {
    showTenantSelector?: boolean;
};

withDefaults(defineProps<AntennaMonitoringContainerPropsType>(), { showTenantSelector: false });

const antennaMonitoringStore = useAntennaMonitoringStore();
const { t } = useTranslation();
const { sendTestData, isConnected, reconnectForTenant } = useAntennaWebSocket(antennaMonitoringStore);
const companyStore = useCompanyStore();
const isLogDialogOpen = ref(false);
const selectedAntennaUuid = ref<string>('');
const isCurrentStateModalOpen = ref(false);
const selectedAntennaForState = ref<string>('');

const handleSendTestData = (antennaUuid: string) => {
    sendTestData(antennaUuid);
};

const handleShowLog = (antennaUuid: string) => {
    selectedAntennaUuid.value = antennaUuid;
    isLogDialogOpen.value = true;
};

const handleShowAllLogs = () => {
    selectedAntennaUuid.value = '';
    isLogDialogOpen.value = true;
};

const handleShowCurrentState = (antennaUuid: string) => {
    selectedAntennaForState.value = antennaUuid;
    isCurrentStateModalOpen.value = true;
};

const handleTableAction = (actionKey: string, antenna: AntennaMonitoringStatsType) => {
    if (actionKey === 'test') {
        handleSendTestData(antenna.antennaUuid);
    }
};

const { antennaStats, recentReadings, totalReadings, totalEpcs } = toRefs(antennaMonitoringStore);

const headers = computed(() => [
    { title: t('antennas.name'), key: 'antennaName', sortable: true },
    { title: t('antennas.uuid'), key: 'antennaUuid', sortable: true },
    { title: t('antennas.readings'), key: 'totalReadings', sortable: true },
    { title: t('antennas.epc'), key: 'totalEpcs', sortable: true },
    { title: t('common.updated'), key: 'lastReading', sortable: true },
    { title: '', key: 'actions', sortable: false },
]);

const customActions = computed(() => [
    {
        key: 'test',
        label: t('common.test'),
        icon: 'mdi-send',
        color: 'primary',
    },
]);

const handleTenantChange = async () => {
    await antennaMonitoringStore.fetchAntennas();
    reconnectForTenant();
};
</script>

<template>
    <div>
        <AntennaCurrentStateModal
            v-model:open="isCurrentStateModalOpen"
            :antenna-uuid="selectedAntennaForState"
            :get-antenna-info="antennaMonitoringStore.getAntennaInfo"
            :get-antenna-readings="antennaMonitoringStore.getAntennaReadings"
        />

        <AntennaLogDialog
            v-model:open="isLogDialogOpen"
            :antenna-uuid="selectedAntennaUuid"
            :readings="antennaMonitoringStore.readings"
            :get-antenna-info="antennaMonitoringStore.getAntennaInfo"
            :get-antenna-readings="antennaMonitoringStore.getAntennaReadings"
        />

        <BRow class="row-monitoring-container">
            <BCol
                cols="12"
                lg="5"
            >
                <BCard
                    variant="flat"
                    class="b-card--flat-w-border mr-2 mb-6"
                >
                    <template #title>
                        <BRow class="d-flex justify-space-between align-center">
                            <BCol
                                cols="12"
                                :sm="showTenantSelector ? 'auto' : '12'"
                                class="d-flex ga-2 align-center flex-wrap"
                                :class="showTenantSelector ? 'justify-sm-start' : ''"
                            >
                                <BChip
                                    :color="isConnected ? 'success' : 'error'"
                                    :prepend-icon="isConnected ? 'mdi-check-circle' : 'mdi-close-circle'"
                                    size="small"
                                >
                                    {{ isConnected ? t('common.connected') : t('common.disconnected') }}
                                </BChip>

                                <BChip
                                    color="primary"
                                    prepend-icon="mdi-counter"
                                    size="small"
                                >
                                    {{ totalReadings }} {{ t('common.readings') }}
                                </BChip>

                                <BChip
                                    color="info"
                                    prepend-icon="mdi-tag-multiple"
                                    size="small"
                                >
                                    {{ totalEpcs }} {{ t('antennas.epc') }}
                                </BChip>
                            </BCol>

                            <BCol
                                v-if="showTenantSelector"
                                cols="12"
                                sm="auto"
                                class="d-flex align-center justify-center justify-sm-end"
                            >
                                <slot name="tenant-selector">
                                    <BAutocomplete
                                        v-model="antennaMonitoringStore.selectedTenantId"
                                        :items="companyStore.items"
                                        item-title="tenant_name"
                                        item-value="tenant_id"
                                        label="antennas.company"
                                        density="compact"
                                        min-width="200px"
                                        max-width="200px"
                                        :hide-details="true"
                                        :loading="companyStore.isLoading"
                                        @update:model-value="handleTenantChange"
                                    />
                                </slot>
                            </BCol>
                        </BRow>
                    </template>

                    <LogList
                        :readings="recentReadings"
                        :show-antenna-name="true"
                        :max-epcs="3"
                        :max-items="5"
                        :empty-message="t('common.noActivity')"
                        empty-icon="mdi-history"
                        container-class="py-3"
                        :get-antenna-name="uuid => antennaMonitoringStore.getAntennaInfo(uuid)?.name || ''"
                        :get-reader-name="uuid => antennaMonitoringStore.getAntennaInfo(uuid)?.reader_name || ''"
                    />

                    <BBtn
                        v-if="recentReadings.length > 0"
                        color="primary"
                        variant="outlined"
                        size="small"
                        class="d-flex mx-auto mt-5"
                        @click="handleShowAllLogs"
                    >
                        {{ t('antennas.allLogs') }}
                    </BBtn>
                </BCard>
            </BCol>
            <BCol
                cols="12"
                lg="7"
            >
                <BCard
                    variant="flat"
                    class="b-card--flat-w-border ml-2 mb-6"
                >
                    <BDataTable
                        :items="antennaStats"
                        :headers="headers"
                        :items-length="antennaStats.length"
                        :loading="antennaMonitoringStore.isLoading"
                        :hide-header="true"
                        :sort-by="[{ key: 'antennaName', order: 'asc' }]"
                        class="bg-transparent"
                    >
                        <template #item.antennaName="{ item }">
                            <strong>{{ item.readerName }} - {{ item.antennaName }}</strong>
                        </template>

                        <template #item.antennaUuid="{ item }">
                            <code class="text-caption">{{ item.antennaUuid }}</code>
                        </template>

                        <template #item.totalReadings="{ item }">
                            {{ item.totalReadings }}
                        </template>

                        <template #item.totalEpcs="{ item }">
                            {{ item.totalEpcs }}
                        </template>

                        <template #item.lastReading="{ item }">
                            <span class="text-caption">{{ item.lastReading || t('common.never') }}</span>
                        </template>

                        <template #item.actions="{ item }">
                            <div class="d-flex ga-2">
                                <BBtn
                                    icon="mdi-antenna"
                                    variant="text"
                                    color="info"
                                    size="small"
                                    density="comfortable"
                                    @click="() => handleShowCurrentState(item.antennaUuid)"
                                >
                                    <BIcon icon="mdi-antenna" />
                                    <BTooltip
                                        activator="parent"
                                        location="top"
                                    >
                                        {{ t('antennas.currentState') }}
                                    </BTooltip>
                                </BBtn>
                                <BBtn
                                    icon="mdi-history"
                                    variant="text"
                                    color="primary"
                                    size="small"
                                    density="comfortable"
                                    @click="() => handleShowLog(item.antennaUuid)"
                                >
                                    <BIcon icon="mdi-history" />
                                    <BTooltip
                                        activator="parent"
                                        location="top"
                                    >
                                        {{ t('antennas.allLogs') }}
                                    </BTooltip>
                                </BBtn>
                                <BTableActions
                                    :show-edit-button="false"
                                    :show-delete-action="false"
                                    :custom-actions="customActions"
                                    @action="handleTableAction($event, item)"
                                />
                            </div>
                        </template>
                    </BDataTable>
                </BCard>
            </BCol>
        </BRow>
    </div>
</template>

<style scoped lang="scss">
.row-monitoring-container {
    margin: 0;
    margin-inline: calc(var(--page-padding-inline) - var(--container-content-padding-inline));
}
.row-monitoring-container > .b-col {
    padding-inline: 0;
}
</style>
